"use client";

import { useState } from "react";

type Lang = "fr" | "en";

const accordionItems: Record<Lang, { title: string; content: string }[]> = {
  fr: [
    {
      title: "Comment puis-je trouver des restaurants de livraison près de moi ?",
      content:
        "Fast App propose une large sélection de restaurants et magasins dans votre ville. Entrez simplement votre adresse ou activez les « services de localisation », et vous pourrez voir quels restaurants livrent près de chez vous.",
    },
    {
      title: "Fast App propose-t-il seulement la livraison de nourriture ?",
      content:
        "Nous offrons également la livraison de courses depuis vos magasins locaux préférés. Aucun téléchargement d'application supplémentaire requis — recherchez directement le magasin et les articles de votre choix dans l'application Fast App.",
    },
    {
      title: "Fast App propose-t-il des recommandations sur quel repas commander ?",
      content:
        "Notre application propose une section « Recommandé » avec les restaurants et magasins les plus populaires de votre ville. Vous pouvez également parcourir les menus et voir les évaluations pour vous assurer de commander les meilleurs plats possibles.",
    },
    {
      title: "Quel type de restaurants est répertorié sur Fast App ?",
      content:
        "Nous proposons une sélection diverse de restaurants et magasins de livraison en ligne tout en veillant à ce qu'ils répondent à nos normes de qualité élevées.",
    },
    {
      title: "Comment puis-je payer la livraison de repas ?",
      content:
        "Vous pouvez payer la livraison de courses ou de repas en espèces ou par carte. Sélectionnez simplement votre mode de paiement préféré lors du paiement.",
    },
    {
      title: "Quels sont les frais pour la livraison de repas ?",
      content:
        "Les articles disponibles à la commande sont individuellement tarifés. Avant de confirmer votre commande, vous pouvez consulter votre panier et voir le total. Les frais suivants peuvent être ajoutés : frais de livraison ; frais de petite commande (pour les commandes en dessous d'un montant minimum).",
    },
  ],
  en: [
    {
      title: "How do I find delivery restaurants near me?",
      content:
        "Fast App offers a wide selection of restaurants and stores in your city. Simply enter your address or enable Location Services to see which restaurants deliver near you.",
    },
    {
      title: "Does Fast App only deliver food?",
      content:
        "We also offer grocery delivery from your favourite local stores. No extra app download needed — just search for the store and items you want directly in the Fast App.",
    },
    {
      title: "Does Fast App recommend what to order?",
      content:
        "Our app features a 'Recommended' section with the most popular restaurants and stores in your city. You can also browse menus and check ratings to make sure you're ordering the best dishes.",
    },
    {
      title: "What types of restaurants are listed on Fast App?",
      content:
        "We offer a diverse selection of restaurants and online delivery stores, while ensuring they all meet our high quality standards.",
    },
    {
      title: "How can I pay for food delivery?",
      content:
        "You can pay for your grocery or food delivery in cash or by card. Simply select your preferred payment method at checkout.",
    },
    {
      title: "What are the fees for food delivery?",
      content:
        "Food items available to order in the app are individually priced. Before confirming your order, you can review your cart and see the total for your chosen items. The following fees may be added: delivery fee; small order fee (for orders below a minimum order value).",
    },
  ],
};

export default function HowItWorksAccordion({ lang = "fr" }: { lang?: Lang }) {
  const [open, setOpen] = useState<number | null>(null);
  const items = accordionItems[lang];

  return (
    <div className="divide-y divide-black/10">
      {items.map((item, i) => (
        <div key={i}>
          <button
            onClick={() => setOpen(open === i ? null : i)}
            className="w-full flex items-center justify-between py-6 text-left group"
            aria-expanded={open === i}
          >
            <span
              className="text-lg md:text-xl font-black tracking-tight leading-snug transition-colors duration-150 group-hover:text-[var(--fast-primary)]"
              style={{ color: open === i ? "var(--fast-primary)" : undefined }}
            >
              {item.title}
            </span>
            <span
              className="ml-4 shrink-0 w-12 h-12 rounded-full border-2 flex items-center justify-center text-xl font-bold transition-all duration-200 group-hover:border-[var(--fast-primary)] group-hover:text-[var(--fast-primary)]"
              style={{
                borderColor: open === i ? "var(--fast-primary)" : undefined,
                color: open === i ? "var(--fast-primary)" : undefined,
              }}
            >
              {open === i ? "−" : "+"}
            </span>
          </button>
          <div
            className="overflow-hidden transition-all duration-300"
            style={{
              maxHeight: open === i ? 300 : 0,
              opacity: open === i ? 1 : 0,
              transition: "max-height 0.6s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.6s ease",
            }}
          >
            <div className="pb-6 pr-16">
              <p className="text-lg text-start md:text-xl leading-relaxed opacity-70">
                {item.content}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
